import { useEffect, useState } from "react";
import {
    AlertTriangle, CheckCircle2, Copy, Download, Loader2, Plus, Save, ShieldCheck, Trash2, Workflow
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateSamlSp, useDeleteSamlSp, useIdpMetadataSummary, useSamlSps, useTenants, useUpdateSamlSp,
    useValidateSamlSp
} from "@/api/hooks";
import { downloadIdpMetadataXml, type SamlServiceProvider } from "@/api/adminBff";
import { useAccessToken } from "@/auth/useAccessToken";

const CERT_WARNING_DAYS = 90;

/**
 * The reverse role from Identity Providers: third-party apps (Salesforce, Slack, AWS IAM Identity
 * Center, GitHub Enterprise) that trust Athavita as THEIR identity provider — the "SSO tile"
 * pattern every enterprise IdP offers. Kept as a separate screen from Identity Providers
 * deliberately: "who trusts us" and "who we trust" are different mental models worth keeping
 * visually distinct, even though the underlying CRUD/validate/enable shape is the same.
 * See EnterpriseIdentity/service/Data/SamlServiceProvider.cs for the full rationale this mirrors.
 */
export function ServiceProvidersPage() {
    const tenants = useTenants();
    const [tenantId, setTenantId] = useState<string | null>(null);

    useEffect(() => {
        if (!tenantId && tenants.data && tenants.data.length > 0) {
            setTenantId(tenants.data[0]!.id);
        }
    }, [tenants.data, tenantId]);

    return (
        <div className="mx-auto max-w-6xl">
            <header className="mb-4 flex items-center gap-2">
                <Workflow className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Service providers</h1>
            </header>
            <p className="mb-4 text-sm text-muted-foreground">
                Register a tenant's external SAML 2.0 service provider — Salesforce, Slack, AWS IAM
                Identity Center, GitHub Enterprise, or any standards-compliant SP that should trust
                Athavita as its identity provider. Once validated and enabled, Athavita issues
                signed sign-in assertions for it.
            </p>

            <div className="mb-4 mt-6">
                <label className="mb-1 block text-xs font-medium">Tenant</label>
                <select
                    value={tenantId ?? ""}
                    onChange={(e) => setTenantId(e.target.value)}
                    className="w-full max-w-sm rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                    <option value="" disabled>Select a tenant…</option>
                    {(tenants.data ?? []).map((t) => (
                        <option key={t.id} value={t.id}>{t.displayName} ({t.slug})</option>
                    ))}
                </select>
            </div>

            {tenantId ? <TenantSps tenantId={tenantId} /> : null}
        </div>
    );
}

/** Per-registration — each registration has its own SSO URL (see SamlServiceProvider's class
 * remarks), so this is the specific document to hand to THIS registration's SP admin. */
function IdpMetadataCard({ id }: { id: string }) {
    const summary = useIdpMetadataSummary(id);
    const token = useAccessToken();
    const [copied, setCopied] = useState(false);

    const daysLeft = summary.data
        ? Math.floor((new Date(summary.data.certificateExpiresAt).getTime() - Date.now()) / 86_400_000)
        : null;

    const handleCopy = async () => {
        if (!summary.data) return;
        await navigator.clipboard.writeText(summary.data.entityId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleDownload = async () => {
        const xml = await downloadIdpMetadataXml(id, token);
        const blob = new Blob([xml], { type: "application/samlmetadata+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "athavita-idp-metadata.xml";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <section className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold">Athavita's IdP metadata</h2>
                <span className="text-xs text-muted-foreground">
                    — hand this to this registration's SP admin
                </span>
            </div>
            {summary.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : summary.data ? (
                <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Entity ID</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.entityId}</code>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy} aria-label="Copy entity ID">
                            {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">SSO URL</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.ssoUrl}</code>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Certificate</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.certificateThumbprint}</code>
                        {daysLeft !== null ? (
                            <span className={daysLeft < CERT_WARNING_DAYS ? "text-xs text-amber-600" : "text-xs text-muted-foreground"}>
                                expires in {daysLeft}d
                            </span>
                        ) : null}
                    </div>
                    <Button variant="outline" size="sm" className="mt-2" onClick={handleDownload}>
                        <Download className="h-3.5 w-3.5" /> Download metadata XML
                    </Button>
                </div>
            ) : (
                <p className="text-sm text-destructive">Could not load IdP metadata.</p>
            )}
        </section>
    );
}

function TenantSps({ tenantId }: { tenantId: string }) {
    const sps = useSamlSps(tenantId);
    const create = useCreateSamlSp(tenantId);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = sps.data?.find((p) => p.id === selectedId) ?? null;

    const [newDisplayName, setNewDisplayName] = useState("");

    const handleCreate = async () => {
        if (!newDisplayName.trim()) return;
        const created = await create.mutateAsync({
            displayName: newDisplayName.trim(),
            metadataUrl: null,
            metadataXml: null,
            enabled: false
        });
        setSelectedId(created.id);
        setNewDisplayName("");
    };

    return (
        <div className="space-y-4">
            <section className="rounded-lg border bg-card p-4 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">New service provider</h2>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                    <Input
                        placeholder='Display name — e.g. "Salesforce (Sales team)"'
                        value={newDisplayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                    />
                    <Button onClick={handleCreate} disabled={create.isPending || !newDisplayName.trim()}>
                        {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Add
                    </Button>
                </div>
                {create.isError ? <p className="mt-2 text-xs text-destructive">{create.error?.message}</p> : null}
            </section>

            <div className="grid grid-cols-[260px_1fr] gap-4">
                <aside className="rounded-lg border bg-card p-2">
                    {sps.isPending ? (
                        <p className="p-3 text-sm text-muted-foreground">Loading…</p>
                    ) : (sps.data ?? []).length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                            {sps.data!.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setSelectedId(p.id)}
                                    className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${
                                        selectedId === p.id ? "bg-accent" : ""
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">{p.displayName}</div>
                                        <div className="truncate text-xs text-muted-foreground">{p.spEntityId ?? "not validated"}</div>
                                    </div>
                                    <StatusBadge sp={p} />
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="p-3 text-sm text-muted-foreground">No service providers registered yet.</p>
                    )}
                </aside>

                {selected ? (
                    <SpDetail key={selected.id} tenantId={tenantId} sp={selected} onDeleted={() => setSelectedId(null)} />
                ) : (
                    <section className="flex items-center justify-center rounded-lg border bg-card p-10 text-sm text-muted-foreground">
                        Select a registration to configure and validate it.
                    </section>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ sp }: { sp: SamlServiceProvider }) {
    if (sp.enabled) {
        return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">enabled</span>;
    }
    if (sp.lastValidationError) {
        return <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">error</span>;
    }
    if (sp.lastValidatedAt) {
        return <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">validated</span>;
    }
    return <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">unvalidated</span>;
}

function SpDetail({
    tenantId,
    sp,
    onDeleted
}: {
    tenantId: string;
    sp: SamlServiceProvider;
    onDeleted: () => void;
}) {
    const update = useUpdateSamlSp(tenantId);
    const validate = useValidateSamlSp(tenantId);
    const remove = useDeleteSamlSp(tenantId);

    // Keyed on sp.id via the parent's `key` prop, so no reset effect needed.
    const [displayName, setDisplayName] = useState(sp.displayName);
    const [metadataSource, setMetadataSource] = useState<"url" | "paste">(sp.metadataUrl ? "url" : "paste");
    const [metadataUrl, setMetadataUrl] = useState(sp.metadataUrl ?? "");
    const [metadataXml, setMetadataXml] = useState(sp.metadataXml ?? "");
    const [enabled, setEnabled] = useState(sp.enabled);

    const dirty =
        displayName !== sp.displayName ||
        (metadataSource === "url" ? metadataUrl : "") !== (sp.metadataUrl ?? "") ||
        (metadataSource === "paste" ? metadataXml : "") !== (sp.metadataXml ?? "") ||
        enabled !== sp.enabled;

    // lastValidationError arrives as `undefined` (not `null`) when the BFF's
    // WhenWritingNull JSON option omits it — a strict `=== null` check would
    // never pass. Falsy covers null/undefined/empty-string uniformly.
    const canEnable = !sp.lastValidationError && sp.lastValidatedAt != null;

    const handleSave = () => {
        update.mutate({
            id: sp.id,
            payload: {
                displayName,
                metadataUrl: metadataSource === "url" ? (metadataUrl.trim() || null) : null,
                metadataXml: metadataSource === "paste" ? (metadataXml.trim() || null) : null,
                enabled
            }
        });
    };

    return (
        <section className="space-y-4">
            <IdpMetadataCard id={sp.id} />

            <div className="rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">{sp.displayName} registration</h3>

                <label className="flex flex-col gap-1 text-xs">
                    <span className="font-medium text-muted-foreground">Display name</span>
                    <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                </label>

                <div className="mt-3">
                    <div className="mb-1 flex items-center gap-3">
                        <span className="text-xs font-medium text-muted-foreground">Metadata source</span>
                        <label className="flex items-center gap-1 text-xs">
                            <input type="radio" checked={metadataSource === "url"} onChange={() => setMetadataSource("url")} />
                            URL
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                            <input type="radio" checked={metadataSource === "paste"} onChange={() => setMetadataSource("paste")} />
                            Paste XML
                        </label>
                    </div>
                    {metadataSource === "url" ? (
                        <Input
                            value={metadataUrl}
                            onChange={(e) => setMetadataUrl(e.target.value)}
                            placeholder="https://your-sp.example.com/.../metadata"
                        />
                    ) : (
                        <textarea
                            className="min-h-[120px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
                            value={metadataXml}
                            onChange={(e) => setMetadataXml(e.target.value)}
                            placeholder="Paste the SP's metadata XML here"
                        />
                    )}
                </div>

                <label className="mt-3 flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={enabled}
                        disabled={!canEnable}
                        onChange={(e) => setEnabled(e.target.checked)}
                    />
                    Enabled
                    {!canEnable ? (
                        <span className="text-xs text-muted-foreground">— validate successfully first</span>
                    ) : null}
                </label>

                <div className="mt-4 flex items-center justify-between">
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => validate.mutate(sp.id)}
                            disabled={validate.isPending || (!sp.metadataUrl && !sp.metadataXml)}
                        >
                            {validate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            Validate
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => {
                                if (window.confirm(`Delete "${sp.displayName}"?`)) {
                                    remove.mutate(sp.id, { onSuccess: onDeleted });
                                }
                            }}
                        >
                            <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                    </div>
                    <Button size="sm" disabled={!dirty || update.isPending} onClick={handleSave}>
                        {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save
                    </Button>
                </div>
                {update.isError ? <p className="mt-2 text-xs text-destructive">{update.error?.message}</p> : null}
            </div>

            <div className="rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">Last validation</h3>
                {sp.lastValidationError ? (
                    <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {sp.lastValidationError}
                    </div>
                ) : null}
                {sp.lastValidatedAt ? (
                    <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Validated</dt>
                        <dd>{new Date(sp.lastValidatedAt).toLocaleString()}</dd>
                        <dt className="text-muted-foreground">SP entity ID</dt>
                        <dd className="truncate"><code>{sp.spEntityId}</code></dd>
                        <dt className="text-muted-foreground">ACS URL</dt>
                        <dd className="truncate"><code>{sp.acsUrl}</code> {sp.acsBinding ? `(${sp.acsBinding})` : ""}</dd>
                        <dt className="text-muted-foreground">Request signing</dt>
                        <dd>{sp.spSigningCertificate ? "certificate on file" : "none (requests not validated)"}</dd>
                    </dl>
                ) : !sp.lastValidationError ? (
                    <p className="text-xs text-muted-foreground">Not validated yet.</p>
                ) : null}
            </div>
        </section>
    );
}
